import _ from "lodash";
import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import CustomSelectClearIndicator from "../common/CustomSelectClearIndicator/CustomSelectClearIndicator";

const LazyLoadedSelect = ({
  loadNextOptions = () => {},
  options = null,
  className = "",
  classNamePrefix = "",
  onChange = () => {},
  name = "",
  placeholder = "Select",
  onInputChange = () => {},
  isLoading = false,
  timezone = false,
  ...props
}) => {
  const selectRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [loadedItemsCount, setLoadedItemsCount] = useState(10);
  const [isSearching, setIsSearching] = useState(false); // To track search mode
  const itemsPerPage = 10;
  let _prevInputValue = null;

  // Handle initial loading of options or timezones
  useEffect(() => {
    if (timezone && Array.isArray(options)) {
      setCurrentOptions(
        options.slice(0, itemsPerPage).map((tz) => ({
          label: tz,
          value: tz,
        }))
      );
    } else {
      setCurrentOptions(options);
    }
  }, [timezone, options]);

  // Load more options initially if timezone is false
  useEffect(() => {
    if (!timezone) loadNextOptions();
  }, []);

  // Handle scrolling to load more options
  useEffect(() => {
    if (!isMenuOpen || !options || !selectRef.current || isSearching) return; // Skip loading more during search

    const menuListRef = selectRef.current?.menuListRef;
    if (!(menuListRef?.scrollHeight > menuListRef?.clientHeight)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          if (timezone) {
            setLoadedItemsCount((prev) => {
              const newCount = prev + itemsPerPage;
              setCurrentOptions(
                options.slice(0, newCount).map((tz) => ({
                  label: tz,
                  value: tz,
                }))
              );
              return newCount;
            });
          } else {
            loadNextOptions();
          }
        }
      },
      { root: menuListRef, threshold: 0.5 }
    );

    const lastItem = menuListRef.childNodes[menuListRef.childNodes.length - 1];
    if (lastItem) observer.observe(lastItem);

    return () => {
      observer.disconnect();
    };
  }, [isMenuOpen, options, currentOptions, isLoading, isSearching]);

  // Handle onChange event
  const handleOnChange = (selectedOption) => {
    onChange(selectedOption);

    // Reset pagination and reload options after selection for timezone case
    if (timezone) {
      setLoadedItemsCount(itemsPerPage);
      setCurrentOptions(
        options.slice(0, itemsPerPage).map((tz) => ({
          label: tz,
          value: tz,
        }))
      );
    }
  };

  // Handle search input
  const handleInputChange = (newValue) => {
    if (newValue !== _prevInputValue) {
      onInputChange(newValue);

      if (timezone) {
        if (newValue) {
          setIsSearching(true); // Enable search mode
          const searchResults = options
            .map((tz) => ({ label: tz, value: tz }))
            .filter((option) =>
              option.label.toLowerCase().includes(newValue.toLowerCase())
            );
          setCurrentOptions(searchResults);
        } else {
          setIsSearching(false); // Exit search mode
          setLoadedItemsCount(itemsPerPage);
          setCurrentOptions(
            options.slice(0, itemsPerPage).map((tz) => ({
              label: tz,
              value: tz,
            }))
          );
        }
      }

      _prevInputValue = newValue;
    }
  };

  const optionalProps = {
    ...(_.has(props, "value") ? { value: props?.value } : {}),
  };
  const otherProps = _.omit(props, _.keys(optionalProps));

  return (
    <Select
      options={currentOptions}
      className={className}
      classNamePrefix={classNamePrefix}
      onChange={handleOnChange}
      name={name}
      placeholder={placeholder}
      onInputChange={handleInputChange}
      ref={selectRef}
      onMenuClose={() => {
        setIsMenuOpen(false);
      }}
      onMenuOpen={() => setIsMenuOpen(true)}
      {...otherProps}
      {...optionalProps}
      components={{ ClearIndicator: CustomSelectClearIndicator }}
      isLoading={isLoading}
    />
  );
};

export default LazyLoadedSelect;
